import { X, Calendar, Clock, MapPin } from 'lucide-react';
import type { Applicant } from '../../../types/applicant';

interface ApplicantDetailsProps {
  applicant: Applicant;
  onClose: () => void;
}

// Helper function to categorize portfolio links
const categorizeLink = (link: string): 'instagram' | 'tiktok' | 'shorts' | 'other' => {
  const lowerLink = link.toLowerCase();
  if (lowerLink.includes('instagram.com')) return 'instagram';
  if (lowerLink.includes('tiktok.com')) return 'tiktok';
  if (lowerLink.includes('youtube.com/shorts') || lowerLink.includes('shorts/')) return 'shorts';
  return 'other';
};

// Helper function to format links
const formatLink = (link: string | null | undefined): string => {
  if (!link) return '';
  if (link.startsWith('http://') || link.startsWith('https://')) return link;
  return `https://${link}`;
};

// Add new helper function to extract username from URL
const extractUsername = (link: string, platform: string): string => {
  const lowerLink = link.toLowerCase();
  try {
    if (platform === 'instagram') {
      return lowerLink.split('instagram.com/')[1]?.split('/')[0] || link;
    }
    if (platform === 'tiktok') {
      return lowerLink.split('tiktok.com/@')[1]?.split('/')[0] || 
             lowerLink.split('tiktok.com/')[1]?.split('/')[0] || link;
    }
    if (platform === 'shorts') {
      return 'YouTube';
    }
    return new URL(link).hostname;
  } catch {
    return link;
  }
};

export function ApplicantDetails({ applicant, onClose }: ApplicantDetailsProps) {
  const portfolioLinks = {
    instagram: applicant.portfolio.filter(link => categorizeLink(link) === 'instagram') || [],
    tiktok: applicant.portfolio.filter(link => categorizeLink(link) === 'tiktok') || [],
    shorts: applicant.portfolio.filter(link => categorizeLink(link) === 'shorts') || [],
    other: applicant.portfolio.filter(link => categorizeLink(link) === 'other') || []
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-end">
      <div className="bg-gray-900 w-full max-w-2xl h-full overflow-y-auto border-l border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-100">
            {`${applicant.firstName} ${applicant.lastName || ''}`}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Basic Contact Information */}
          <section>
            <h3 className="text-lg font-medium text-gray-100 mb-4">Contact Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Phone</p>
                  <p className="text-sm font-medium text-gray-200">{applicant.phone}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Discord</p>
                  <p className="text-sm font-medium text-gray-200">{applicant.discord}</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-400">Location</p>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-500 mr-1" />
                  <p className="text-sm font-medium text-gray-200">{applicant.location}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Content & Portfolio */}
          <section>
            <h3 className="text-lg font-medium text-gray-100 mb-4">Content & Portfolio</h3>
            <div className="space-y-6">
              {/* Portfolio Links */}
              <div className="space-y-4">
                <p className="text-sm text-gray-400 font-medium">Portfolio Links</p>
                {Object.entries(portfolioLinks).map(([platform, links]) => (
                  links.length > 0 && (
                    <div key={platform} className="space-y-2">
                      <p className="text-xs text-gray-500 capitalize">{platform}</p>
                      <div className="flex flex-wrap gap-2">
                        {links.map((link, index) => {
                          const username = extractUsername(link, platform);
                          const pillColor = {
                            instagram: 'bg-pink-900/50 text-pink-200 border-pink-700',
                            tiktok: 'bg-purple-900/50 text-purple-200 border-purple-700',
                            shorts: 'bg-red-900/50 text-red-200 border-red-700',
                            other: 'bg-blue-900/50 text-blue-200 border-blue-700'
                          }[platform];

                          return (
                            <a
                              key={index}
                              href={formatLink(link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${pillColor} hover:opacity-80 transition-opacity`}
                            >
                              {platform === 'instagram' && '@'}
                              {platform === 'tiktok' && '@'}
                              {username}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </div>

              {/* Best Content */}
              {applicant.bestContent && applicant.bestContent.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 font-medium">Best Content</p>
                  <div className="flex flex-wrap gap-2">
                    {applicant.bestContent.map((link, index) => (
                      <a
                        key={index}
                        href={formatLink(link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-gray-800/50 text-gray-200 border-gray-700 hover:opacity-80 transition-opacity"
                      >
                        {`Video ${index + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Performance Metrics */}
              <div className="space-y-4">
                <p className="text-sm text-gray-400 font-medium">Performance Metrics</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Client Count</p>
                    <p className="text-sm font-medium text-gray-200 mt-1">{applicant.clientCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Lifetime Views</p>
                    <p className="text-sm font-medium text-gray-200 mt-1">{applicant.lifetimeViews}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Lifetime GMV</p>
                    <p className="text-sm font-medium text-gray-200 mt-1">{applicant.lifetimeGmv}</p>
                  </div>
                </div>
              </div>

              {/* Creator Details */}
              <div className="space-y-4">
                <p className="text-sm text-gray-400 font-medium">Creator Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Languages Spoken</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {applicant.languagesSpoken.map((language) => (
                        <span
                          key={language}
                          className="px-2 py-1 text-xs font-medium bg-green-900/50 text-green-200 rounded border border-green-700"
                        >
                          {language}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Shows Face</p>
                    <p className="text-sm font-medium text-gray-200 mt-1">
                      {applicant.showsFace ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Referral Source</p>
                    <p className="text-sm text-gray-200 mt-1">{applicant.referralSource}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Application Status */}
          <section>
            <h3 className="text-lg font-medium text-gray-100 mb-4">Application Status</h3>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                  <div>
                    <p className="text-sm text-gray-400">Submission Date</p>
                    <p className="text-sm font-medium text-gray-200">
                      {new Date(applicant.submissionDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-500 mr-2" />
                  <div>
                    <p className="text-sm text-gray-400">Stage Last Updated</p>
                    <p className="text-sm font-medium text-gray-200">
                      {new Date(applicant.stageLastUpdated).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Offers Section */}
          {applicant.offers && applicant.offers.length > 0 && (
            <section>
              <h3 className="text-lg font-medium text-gray-100 mb-4">Offers</h3>
              <div className="space-y-4">
                {applicant.offers.map((offer, index) => (
                  <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-400">Retainer Amount</p>
                        <p className="text-sm font-medium text-gray-200">${offer.retainerAmount}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Commission</p>
                        <p className="text-sm font-medium text-gray-200">{offer.commission}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Status</p>
                        <p className="text-sm font-medium text-gray-200">{offer.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Sent Date</p>
                        <p className="text-sm font-medium text-gray-200">
                          {offer.sentAt ? new Date(offer.sentAt).toLocaleDateString() : 'Not sent'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}